/**
 * Charges — reading, writing and collecting them.
 *
 * A charge is money taken from a customer that is not settling a deal: a
 * deposit on a car nobody has written up yet, a fee collected after the fact,
 * a repair the customer agreed to pay for. Deal money stays in `payments`,
 * where the balance checks belong; see 32_charges.sql for why the two cannot
 * be one table.
 *
 * Nothing here ever sees a card number. Collecting goes through the same
 * Stripe iframe the deal screen uses, and the amount is read back out of the
 * database by the worker on every path, so neither this file nor a tampered
 * copy of it can decide what gets charged.
 */

import { supabase } from "../../lib/supabase";
import { newToken, sha256Hex } from "../../lib/signing/remote";
import {
  emailChargeReceipt, refundCharge as refundViaWorker, CARD_MAX_CENTS,
} from "../../lib/stripe";
import { buildChargeReceipt, type ChargeReceiptData } from "../../forms/chargeReceipt";
import { BUCKET } from "../../lib/documents";

export type ChargeStatus = "open" | "paid" | "refunded" | "voided";

/** One row of the charge_list view, which has the joins already done. */
export interface ChargeRow {
  id: string;
  charge_no: string;
  amount_cents: number;
  description: string | null;
  status: ChargeStatus;
  card_brand: string | null;
  card_last4: string | null;
  paid_at: string | null;
  refunded_at: string | null;
  created_at: string;
  link_sent_at: string | null;
  link_expires_at: string | null;
  link_to_email: string | null;
  has_link: boolean;
  link_expired: boolean;
  customer_id: string;
  customer_name: string;
  customer_email: string | null;
  customer_phone: string | null;
  vehicle_id: string | null;
  vehicle: string | null;
  vin: string | null;
  stock_no: string | null;
}

export interface ChargeEvent {
  id: number;
  at: string;
  kind: string;
  detail: Record<string, unknown> | null;
  ip: string | null;
}

export const STATUS_LABEL: Record<ChargeStatus, string> = {
  open: "Unpaid",
  paid: "Paid",
  refunded: "Refunded",
  voided: "Cancelled",
};

const EVENT_LABEL: Record<string, string> = {
  created: "Charge created",
  link_sent: "Payment link emailed",
  link_opened: "Customer opened the link",
  link_cancelled: "Payment link cancelled",
  paid: "Paid",
  refunded: "Refunded",
  voided: "Cancelled",
  receipt_printed: "Receipt printed",
  receipt_emailed: "Receipt emailed",
  notify_failed: "Notification email failed",
};

export const labelForEvent = (kind: string) =>
  EVENT_LABEL[kind] ?? kind.replace(/_/g, " ");

/** Best effort. A history that cannot be written must not block the money. */
async function logEvent(
  chargeId: string, kind: string, detail?: Record<string, unknown>,
) {
  const { data: user } = await supabase.auth.getUser();
  const { error } = await supabase.from("charge_events").insert({
    charge_id: chargeId,
    kind,
    actor: user?.user?.email ?? null,
    detail: detail ?? null,
  });
  if (error) console.warn("charge event not recorded", kind, error.message);
}

/* ------------------------------------------------------------------ */
/* Reading                                                             */
/* ------------------------------------------------------------------ */

export async function listCharges(): Promise<ChargeRow[]> {
  const { data, error } = await supabase
    .from("charge_list")
    .select("*")
    .order("created_at", { ascending: false });
  if (error) throw new Error(`Could not load payments: ${error.message}`);
  return (data ?? []) as ChargeRow[];
}

export async function getCharge(id: string): Promise<ChargeRow> {
  const { data, error } = await supabase
    .from("charge_list").select("*").eq("id", id).maybeSingle();
  if (error) throw new Error(`Could not load the payment: ${error.message}`);
  if (!data) throw new Error("That payment no longer exists.");
  return data as ChargeRow;
}

export async function chargeHistory(id: string): Promise<ChargeEvent[]> {
  const { data, error } = await supabase
    .from("charge_events")
    .select("id,at,kind,detail,ip")
    .eq("charge_id", id)
    .order("at");
  if (error) return [];
  return (data ?? []) as ChargeEvent[];
}

/** What a customer has paid outside their deals, newest first. */
export async function chargesForCustomer(customerId: string): Promise<ChargeRow[]> {
  const { data, error } = await supabase
    .from("charge_list").select("*")
    .eq("customer_id", customerId)
    .order("created_at", { ascending: false });
  if (error) return [];
  return (data ?? []) as ChargeRow[];
}

/* ------------------------------------------------------------------ */
/* Writing                                                             */
/* ------------------------------------------------------------------ */

export async function createCharge(input: {
  customerId: string;
  amountCents: number;
  /** Both optional by design — see the note at the top of 32_charges.sql. */
  vehicleId?: string | null;
  description?: string | null;
}): Promise<ChargeRow> {
  if (!input.customerId) throw new Error("Pick a customer.");
  if (!Number.isInteger(input.amountCents) || input.amountCents <= 0) {
    throw new Error("Enter an amount.");
  }
  if (input.amountCents > CARD_MAX_CENTS) {
    throw new Error(
      `Card charges are capped at $${(CARD_MAX_CENTS / 100).toLocaleString("en-US")}.`);
  }

  const { data: user } = await supabase.auth.getUser();
  const { data, error } = await supabase
    .from("charges")
    .insert({
      customer_id: input.customerId,
      vehicle_id: input.vehicleId || null,
      description: input.description?.trim() || null,
      amount_cents: input.amountCents,
      created_by: user?.user?.id ?? null,
    })
    .select("id")
    .single();

  if (error || !data) {
    throw new Error(`Could not create the payment: ${error?.message ?? "no row"}`);
  }
  await logEvent(data.id, "created", {
    amount_cents: input.amountCents,
    has_vehicle: Boolean(input.vehicleId),
  });
  return getCharge(data.id);
}

/** Cancel an unpaid charge. Paid ones are refunded, never cancelled. */
export async function voidCharge(id: string, reason: string): Promise<void> {
  const { data, error } = await supabase
    .from("charges")
    .update({
      status: "voided",
      void_reason: reason.trim() || null,
      // A cancelled charge's link must stop working, and the row is the only
      // place that can say so: the token itself was never stored.
      token_hash: null,
    })
    .eq("id", id)
    .eq("status", "open")
    .select("id");

  if (error) throw new Error(`Could not cancel it: ${error.message}`);
  if (!data?.length) {
    throw new Error("That payment is no longer open — reload the page to see where it got to.");
  }
  await logEvent(id, "voided", { reason: reason.trim() || null });
}

/** Full refund through Stripe, then the row. The worker does both. */
export async function refund(id: string, reason: string): Promise<void> {
  await refundViaWorker(id, reason);
}

/* ------------------------------------------------------------------ */
/* The payment link                                                    */
/* ------------------------------------------------------------------ */

export interface SentPaymentLink {
  url: string;
  expiresAt: string;
}

/**
 * Email the customer a link to pay.
 *
 * Worth being straight about what this costs. A charge requires an existing
 * customer because on a chargeback the only question anyone asks is which
 * person this card belongs to — and a link payment records the charge against
 * that customer while the card that paid could belong to whoever holds the
 * URL. All the history can say is which address it was emailed to and the IP
 * that opened it. The desk path is the stronger one; this is the one that
 * gets the money when the customer is in Ohio.
 *
 * One secret, not two. The signing flow pairs a link with a code read out
 * over the telephone, because a leaked signing link puts somebody else's mark
 * on a title application. A leaked payment link lets a stranger pay us money,
 * which is not a risk worth making every customer phone in for — so the token
 * stands alone. It is still 160 bits, still stored only as a hash, and still
 * expires.
 *
 * Replacing a link revokes the old one: the row holds one hash, so writing a
 * new one makes every URL sent before this moment stop working.
 */
export async function sendPaymentLink(
  charge: ChargeRow,
  to: string,
  opts: { expiresInHours?: number } = {},
): Promise<SentPaymentLink> {
  const address = to.trim().toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address)) {
    throw new Error("That does not look like an email address.");
  }
  if (charge.status !== "open") {
    throw new Error(`${charge.charge_no} is ${STATUS_LABEL[charge.status].toLowerCase()} — there is nothing to pay.`);
  }

  const token = newToken();
  const hours = opts.expiresInHours ?? 168;      // a week
  const expiresAt = new Date(Date.now() + hours * 3600_000).toISOString();

  const { data: stamped, error } = await supabase
    .from("charges")
    .update({
      token_hash: await sha256Hex(token),
      link_to_email: address,
      link_sent_at: new Date().toISOString(),
      link_expires_at: expiresAt,
    })
    .eq("id", charge.id)
    .eq("status", "open")
    .select("id");
  if (error) throw new Error(`Could not create the link: ${error.message}`);
  /* The status came off a row read when the page loaded. If it has been paid
     or cancelled since, nothing was stamped — and sending the email anyway
     would put a link in the customer's inbox that can never work. */
  if (!stamped?.length) {
    throw new Error(
      "Nothing was sent: this payment is no longer open. Reload the page to "
      + "see where it got to.");
  }

  const url = `${window.location.origin}/?pay=${token}`;
  const dealer = await dealerName();
  const amount = (charge.amount_cents / 100)
    .toLocaleString("en-US", { style: "currency", currency: "USD" });

  /* The row holds one hash, so stamping it above already killed whatever link
     was sent before this moment. If the email now fails, saying so plainly is
     not optional: the clerk has to know the customer's old link is dead and
     that this new one exists and can be read out. Letting the error escape
     bare would leave them to guess. */
  try {
    await emailChargeReceipt({
    to: address,
    subject: `Payment request — ${amount}`,
    message:
      `Hi ${charge.customer_name.split(" ")[0] || "there"},\n\n`
      + `You can pay ${amount}`
      + (charge.description ? ` for ${charge.description}` : "")
      + ` using the secure link below.\n\n`
      + `${url}\n\n`
      + `The link works until ${new Date(expiresAt).toLocaleString()}. `
      + `Your card details go straight to our payment processor — we never see them.\n\n`
      + `Reference ${charge.charge_no}.\n\n`
      + `Thanks,\n${dealer}`,
      // No attachment: there is nothing to attach until it has been paid.
      chargeId: charge.id,
      context: {
        ...(charge.vehicle ? { vehicle: charge.vehicle } : {}),
        ...(charge.vin ? { vin: charge.vin } : {}),
      },
    });
  } catch (e) {
    await logEvent(charge.id, "link_email_failed", {
      to: address,
      error: e instanceof Error ? e.message : String(e),
    });
    throw new Error(
      `The link was created but the email did not send: `
      + `${e instanceof Error ? e.message : "unknown error"}. `
      + `Read it to the customer instead — ${url} — and note that any link `
      + `sent before now has stopped working.`);
  }

  await logEvent(charge.id, "link_sent", { to: address, expires_at: expiresAt });
  return { url, expiresAt };
}

/** Make every link sent so far stop working, without cancelling the charge. */
export async function cancelPaymentLink(id: string): Promise<void> {
  const { data, error } = await supabase
    .from("charges")
    .update({ token_hash: null, link_expires_at: null })
    .eq("id", id)
    .eq("status", "open")
    .select("id");
  if (error) throw new Error(`Could not cancel the link: ${error.message}`);
  /* Zero rows comes back as no data and no error, so without this check the
     one case that matters — the customer paid a moment ago and the poll has
     not fired — would report success while the link stayed live. */
  if (!data?.length) {
    throw new Error(
      "The link was not cancelled: this payment is no longer open. Reload the "
      + "page to see where it got to.");
  }
  await logEvent(id, "link_cancelled");
}

/* ------------------------------------------------------------------ */
/* The receipt                                                         */
/* ------------------------------------------------------------------ */

async function dealerName(): Promise<string> {
  const { data } = await supabase
    .from("dealer_settings").select("legal_name,dba").maybeSingle();
  return data?.dba || data?.legal_name || "the dealership";
}

/**
 * dealer_settings is one row and every column is NOT NULL, so the only two
 * states are complete and absent. Absent has to fail here rather than degrade:
 * a receipt with no dealer name, address, phone or licence number on it still
 * looks like a receipt, and both the customer's copy and the filed one would
 * go out that way with nothing said.
 */
const NO_DEALER =
  "Settings has no dealership details yet, and a receipt without a name, "
  + "address and licence number on it is not a receipt. Fill in Settings first.";

/** Everything the receipt needs, read in one go. */
export async function receiptDataFor(charge: ChargeRow): Promise<ChargeReceiptData> {
  const [{ data: d }, { data: cu }] = await Promise.all([
    supabase.from("dealer_settings")
      .select("legal_name,dba,street,city,state,zip,phone,email,flhsmv_license_no")
      .maybeSingle(),
    supabase.from("customers")
      .select("street,city,state,zip").eq("id", charge.customer_id).maybeSingle(),
  ]);

  if (!d) throw new Error(NO_DEALER);

  return {
    chargeNo: charge.charge_no,
    amountCents: charge.amount_cents,
    description: charge.description,
    customerName: charge.customer_name,
    customerAddress: cu
      ? [cu.street, [cu.city, cu.state].filter(Boolean).join(", "), cu.zip]
          .filter(Boolean).join(" ") || null
      : null,
    vehicle: charge.vehicle,
    vin: charge.vin,
    cardBrand: charge.card_brand,
    cardLast4: charge.card_last4,
    paidAt: charge.paid_at,
    refundedAt: charge.refunded_at,
    dealer: {
      legalName: d.legal_name, dba: d.dba,
      street: d.street, city: d.city, state: d.state, zip: d.zip,
      phone: d.phone, email: d.email,
      flhsmvLicenseNo: d.flhsmv_license_no,
    },
  };
}

export async function buildReceipt(charge: ChargeRow): Promise<Uint8Array> {
  if (charge.status === "open" || charge.status === "voided") {
    throw new Error(`${charge.charge_no} has not been paid, so there is nothing to receipt.`);
  }
  return buildChargeReceipt(await receiptDataFor(charge));
}

/**
 * File the receipt against the charge.
 *
 * Upload first, row second, exactly as deal documents are filed. A failed row
 * leaves an object nothing points at, which is invisible to the app and
 * counts against storage for ever — the removal below tries to clean it up
 * and cannot, for the reason given there.
 *
 * Best effort from the caller's point of view. The customer has their receipt
 * either way; a failure here is worth showing and not worth refusing to print
 * over.
 */
export async function archiveReceipt(
  bytes: Uint8Array, charge: ChargeRow,
): Promise<void> {
  const stamp = new Date().toISOString().replace(/[:.]/g, "-");
  const path = `charges/${charge.id}/receipt-${stamp}.pdf`;

  const buffer = new ArrayBuffer(bytes.byteLength);
  new Uint8Array(buffer).set(bytes);
  const blob = new Blob([buffer], { type: "application/pdf" });

  const up = await supabase.storage
    .from(BUCKET).upload(path, blob, { contentType: "application/pdf", upsert: false });
  if (up.error) throw new Error(`Printed, but not filed: ${up.error.message}`);

  const { error } = await supabase.from("documents").insert({
    charge_id: charge.id,
    doc_type: "charge_receipt",
    storage_path: path,
    byte_size: blob.size,
    generated_at: new Date().toISOString(),
  });
  if (error) {
    /* Tried, and it will not work: 10_document_archive.sql deliberately grants
       no DELETE policy on the bucket, so the uploaded object stays behind with
       nothing pointing at it. Attempted anyway in case that policy is ever
       added, and named in the message so the orphan is known about rather
       than discovered when storage fills up. */
    const gone = await supabase.storage.from(BUCKET).remove([path]);
    throw new Error(
      `Printed, but not filed: ${error.message}.`
      + (gone.error ? ` A copy is left in storage at ${path}.` : ""));
  }
  await logEvent(charge.id, "receipt_printed", { path });
}

/** Email the receipt as a PDF attachment. */
export async function mailReceipt(
  charge: ChargeRow, bytes: Uint8Array, to: string,
): Promise<void> {
  const address = to.trim().toLowerCase();
  if (!/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(address)) {
    throw new Error("That does not look like an email address.");
  }
  const amount = (charge.amount_cents / 100)
    .toLocaleString("en-US", { style: "currency", currency: "USD" });
  const dealer = await dealerName();

  await emailChargeReceipt({
    to: address,
    subject: `Receipt — ${amount} (${charge.charge_no})`,
    message:
      `Hi ${charge.customer_name.split(" ")[0] || "there"},\n\n`
      + `Your receipt for ${amount}`
      + (charge.description ? ` — ${charge.description}` : "")
      + ` is attached.\n\n`
      + `Reference ${charge.charge_no}. Keep it for your records.\n\n`
      + `Thanks,\n${dealer}`,
    filename: `${charge.charge_no}-receipt.pdf`,
    pdfBase64: toBase64(bytes),
    chargeId: charge.id,
    context: {
      ...(charge.vehicle ? { vehicle: charge.vehicle } : {}),
      ...(charge.vin ? { vin: charge.vin } : {}),
    },
  });
}

/** Chunked, because String.fromCharCode(...bytes) blows the stack on a PDF. */
export function toBase64(bytes: Uint8Array): string {
  let s = "";
  const CHUNK = 0x8000;
  for (let i = 0; i < bytes.length; i += CHUNK) {
    s += String.fromCharCode(...bytes.subarray(i, i + CHUNK));
  }
  return btoa(s);
}
